/** @jsxImportSource @emotion/react */
import React, {
  Fragment,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
  useState,
} from 'react';

import { useOnboardingDataContext } from '../../context/OnboardingData';
import disabilitiesReducer from '../../reducers/disabilities';
import getNextQuestionId from '../../utils/getNextQuestionId';
import idFromString from '../../utils/idFromString';
import useStyles from './styles';

const MultipleQuestion = React.forwardRef(
  (
    {
      readableId,
      answers,
      ageRanges,
      onAnswer,
      selectedAge,
      headerTitle,
      nextClickAnswer,
      questionOrder,
    },
    disabilityRef
  ) => {
    const styles = useStyles();
    const [answersState, dispatch] = useReducer(disabilitiesReducer, []);
    const {
      OnboardingContext,
      setOnboardingDataContext,
      arrowContext,
      setArrowFunction,
    } = useOnboardingDataContext();
    const [selectedAnswerValue, setSelectedAnswerValue] = useState();

    // Ref for the main scrollable container
    const containerRef = useRef(null);

    const onChange = event => {
      window.scrollTo(0, 0);
      setArrowFunction(true);
      const id = parseInt(event.target.value, 10);
      if (event.target.checked) {
        dispatch({ type: 'add', payload: { id } });
      } else {
        dispatch({ type: 'remove', payload: { id } });
      }
    };

    // Refined handler to automatically scroll the focused item into view before selection
    const handleFocus = event => {
      // Use the standard browser scrollIntoView method on the input element's container (the label)
      // 'center' is usually best for tabbing navigation
      event.target.parentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    };

    const disabilityOption = () => {
      setTimeout(() => {
        onAnswer({
          answerData: { [readableId]: answersState },
          nextQuestionId: getNextQuestionId({ ageRanges, selectedAge }),
        });
      }, 500);
    };

    useImperativeHandle(disabilityRef, () => ({
      disabilityOption,
    }));

    useEffect(() => {
      if (!answers) {
        return;
      }
      if (answersState?.length === 0) {
        setArrowFunction(false);
      }
      // filter value of selected answer
      const selectedAnswers = answers
        .filter(item => {
          return answersState.some(ans => item.answerId === ans.DisabilityId);
        })
        .map(ele => ele.value)
        .toString();
      setSelectedAnswerValue(selectedAnswers);
    }, [answers, answersState, setArrowFunction]);
    // set basic data in onboardingContext
    useEffect(() => {
      setOnboardingDataContext({
        ...OnboardingContext,
        question: headerTitle,
        step: 2,
        disability: selectedAnswerValue,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAnswerValue, headerTitle]);

    // --- Scroll effects logic (remains mostly the same, using containerRef) ---
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const timer = setTimeout(() => {
        container.scrollTop += 1;
        container.addEventListener('scroll', handleScroll);
        return () => {
          clearTimeout(timer);
          container.removeEventListener('scroll', handleScroll);
        };
      }, 300);
      return () => clearTimeout(timer);
    }, []);

    const handleScroll = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const blurZoneHeight = container.clientHeight * 0.4;
      const blurZoneTop = containerRect.bottom - blurZoneHeight;
      // Check if the user has scrolled to the bottom (allowing a small margin)
      const bottomscroll =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 1;
      const labels = container.getElementsByTagName('label');
      const options = [];
      for (let i = 0; i < labels.length; i++) {
        options.push(labels[i]);
      }
      if (bottomscroll) {
        options.forEach(option => {
          option.classList.remove('fade');
          option.style.opacity = '1';
        });
        return;
      }
      // For each option, apply a progressive fade if it falls into the blur zone
      options.forEach(option => {
        const optionRect = option.getBoundingClientRect();
        // If the top of the option is within the blur zone, calculate fade amount
        if (optionRect.top >= blurZoneTop) {
          // Calculate a progress value (0 at the top of the blur zone, 1 at the very bottom)
          const progress = (optionRect.top - blurZoneTop) / blurZoneHeight;
          // Invert progress so that items closer to bottom get more fade
          const invertedProgress = 1 - progress;
          // Set opacity between 0.5 (minimal) and 1 (maximum)
          const minOpacity = 0.5,
            maxOpacity = 1;
          const opacityValue =
            minOpacity + (maxOpacity - minOpacity) * invertedProgress;
          option.classList.add('fade');
          option.style.opacity = opacityValue;
        } else {
          // Items above the blur zone remain clear
          option.classList.remove('fade');
          option.style.opacity = '1';
        }
      });
    };

    return (
      <Fragment>
        <div
          id="disability-div"
          ref={containerRef}
          onScroll={handleScroll}
          css={styles.base}
          tabIndex={-1}
        >
          {answers &&
            answers.map(({ answerId, value }) => {
              const optionId = idFromString(value);
              const isChecked = !!answersState.find(
                answer => answer.DisabilityId === parseInt(answerId, 10)
              );

              return (
                <label css={styles.label} key={answerId}>
                  <input
                    tabIndex={0}
                    css={styles.checkbox}
                    type="checkbox"
                    id={optionId}
                    name={optionId}
                    value={answerId}
                    onChange={onChange}
                    onFocus={handleFocus}
                    role='button'
                    aria-pressed={isChecked}
                  />
                  <span css={styles.answer(isChecked)}>{value}</span>
                </label>
              );
            })}
        </div>
        <button
          id={`question_${questionOrder}_next`}
          aria-label="Next"
          css={styles.buttonNext(arrowContext)}
          // disabled={answersState?.length === 0 ? true : false}
          disabled={arrowContext === false ? true : false}
          onClick={() => {
            nextClickAnswer();
            disabilityOption();
            setArrowFunction(false);
          }}
        >
          Next
        </button>
      </Fragment>
    );
  }
);

export default MultipleQuestion;
